import axios from 'axios'
import { storage, API_BASE_URL, SESSION_API_BASE_URL } from 'utils'
import { endVeraSpaceSession } from 'stores'
import { ErrorCode, AuthFactorType, type IAuthChallengeState, type IAuthSmsOptedOut } from '@repo/types'
import { endPlatformSession, renewPlatformSession } from './platformSession'

/** Opt-in to server-side sessions on the login calls (see verifyMagicAuthCode). */
const PLATFORM_SESSION_HEADERS = { 'X-Vera-Session': '1' }
/** The same opt-in for the SSO redirect, which cannot carry a header: rides OAuth `state`. */
const PLATFORM_SESSION_PARAM = 'session=1'

const API_URL = API_BASE_URL
/**
 * Every call whose response SETS the HttpOnly session cookie goes to the same-site API host,
 * or the browser drops the cookie (SameSite=Strict on a cross-site `*.run.app` response).
 * See `resolveSessionApiBaseUrl` in utils/apiUrl.ts.
 */
const SESSION_API_URL = SESSION_API_BASE_URL

/**
 * VRG-490 §2 — the discriminants a login/verify call can return in place of
 * a normal token response, once a verified second factor exists. Spread
 * onto the loose return shapes below (matching this file's existing
 * convention of optional fields rather than a strict union) so a caller can
 * branch on `result.challengeRequired` / `result.enrollmentRequired` without
 * a type guard.
 */
export interface TwoFactorChallengeFields {
  challengeRequired?: boolean
  ticket?: string
  factorType?: AuthFactorType
  /** SMS only — `***4567`. */
  maskedPhone?: string
  /** Every factor type this sign-in may be completed with; >1 offers "use another method". */
  availableFactorTypes?: AuthFactorType[]
  /** The challenged SMS number replied STOP, so no code was texted (the ticket still works). */
  smsOptedOut?: IAuthSmsOptedOut
  enrollmentRequired?: boolean
}

export interface WorkOSUser {
  id: string
  companyId?: string | null
  workosId?: string | null
  email: string
  firstName?: string
  lastName?: string
  fullName?: string
  username?: string
  role: {
    id?: string | null
    name?: string | null
    permissions: string[]
  }
  company: {
    id?: string | null
    name?: string | null
    workosOrgId?: string | null
  } | null
  lastSsoLogin?: Date
  ssoProvider?: string
}

/**
 * WorkOS AuthKit Service
 * Simplified service for WorkOS AuthKit authentication
 * Authentication flow is handled by redirecting to AuthKit hosted UI
 */
class WorkOSAuthService {
  /**
   * Get current user profile from API
   */
  async getProfile(): Promise<WorkOSUser | null> {
    try {
      let token = this.getToken()
      if (!token) {
        return null
      }

      const fetchProfile = (bearer: string) =>
        axios.get(`${API_URL}/auth/profile`, {
          headers: {
            Authorization: `Bearer ${bearer}`,
          },
        })

      let response
      try {
        response = await fetchProfile(token)
      } catch (error) {
        // The 15-minute access token expired (e.g. a reload after a coffee break): renew
        // silently from the session cookie once, then retry. Anything else is a real failure.
        if (!(axios.isAxiosError(error) && error.response?.status === 401)) throw error
        const outcome = await renewPlatformSession(token)
        if (outcome.status !== 'renewed') throw error
        token = outcome.token
        response = await fetchProfile(token)
      }

      const raw = response.data?.user ?? response.data
      if (!raw?.id) {
        return null
      }

      const companies = raw.companies ?? []
      const firstCompany = companies[0]
      const perms = raw.role?.permissions ?? []
      const permissionNames: string[] = perms
        .map((p: string | { name?: string }) =>
          typeof p === 'string' ? p : p?.name
        )
        .filter(Boolean)

      return {
        id: raw.id,
        email: raw.email,
        firstName: raw.firstName,
        lastName: raw.lastName,
        fullName: raw.fullName,
        username: raw.username,
        companyId: firstCompany?.id ?? raw.companyId ?? null,
        workosId: raw.workosId ?? null,
        role: {
          id: raw.role?.id ?? null,
          name: raw.role?.name ?? null,
          permissions: permissionNames,
        },
        company:
          raw.company ??
          (firstCompany
            ? {
                id: firstCompany.id,
                name: firstCompany.name ?? null,
                workosOrgId:
                  firstCompany.workos_org_id ?? firstCompany.workosOrgId ?? null,
              }
            : null),
        lastSsoLogin: raw.lastSsoLogin,
        ssoProvider: raw.ssoProvider,
      }
    } catch (error) {
      console.error('Failed to get profile:', error)
      // Only a 401 means the sign-in is invalid. A 5xx or network failure (deploy, DB blip)
      // must NOT sign the user out — rethrow so the caller treats it as "could not check".
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      if (status !== 401) throw error
      this.logout()
      return null
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!storage.getItem('accessToken')
  }

  /**
   * Logout user
   * Revokes the WorkOS session and clears local storage
   */
  async logout(): Promise<void> {
    try {
      // End OUR session first — that is what stops this device's access server-side and
      // clears the HttpOnly cookie. The WorkOS logout below only affects WorkOS.
      await endPlatformSession()

      const token = this.getToken()

      // If we have a token, try to revoke the session on the backend
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/auth/logout`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            maxRedirects: 0, // Don't follow redirects automatically
            validateStatus: (status) => status < 400 || status === 302, // Accept redirects
          })

          // If the server returns a logout URL, redirect to it
          // This is handled by the API which may redirect directly
          console.log('Logout successful on backend')
        } catch (error) {
          // If the request fails, still proceed with local logout
          console.warn('Failed to logout on backend, clearing local session:', error)
        }
      }
    } finally {
      // Always clear local storage, even if backend logout fails
      storage.removeItem('accessToken')
      storage.removeItem('refreshToken')
      storage.removeItem('selectedCompany')
      // VRG-661: signing out ends any company space too. It is persisted in localStorage
      // and outlived every sign-out, so the next sign-in resumed it.
      endVeraSpaceSession()
    }
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return storage.getItem('accessToken')
  }

  /**
   * Send Magic Auth (OTP) code to user's email
   */
  async sendMagicAuthCode(email: string): Promise<{ success: boolean; message?: string; error?: string }> {
    console.log('[WorkOS Auth] Sending Magic Auth code to:', email)
    try {
      const response = await axios.get(`${API_URL}/auth/magic-auth/send`, {
        params: { email },
      })

      console.log('[WorkOS Auth] Magic Auth code sent successfully:', response.data)
      return {
        success: response.data.success,
        message: response.data.message,
      }
    } catch (error) {
      console.error('[WorkOS Auth] Failed to send Magic Auth code:', error)
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to send verification code'

      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  /**
   * Verify Magic Auth (OTP) code and authenticate user
   */
  async verifyMagicAuthCode(email: string, code: string): Promise<{ success: boolean; user?: WorkOSUser; isNewUser?: boolean; error?: string } & TwoFactorChallengeFields> {
    // Never log the one-time code: session-replay/console capture would ship it off the device.
    try {
      // withCredentials: this response sets the HttpOnly session cookie, and a cross-origin
      // XHR only stores Set-Cookie when the request was made with credentials.
      const response = await axios.get(`${SESSION_API_URL}/auth/magic-auth/verify`, {
        params: { email, code },
        withCredentials: true,
        // Tells the API this client can renew a server-side session (else it issues a
        // legacy 24h token — see PlatformLoginOptions in the API's auth.service.ts).
        headers: PLATFORM_SESSION_HEADERS,
      })

      console.log('[WorkOS Auth] Verify response:', {
        success: response.data.success,
        hasTokens: !!response.data.tokens,
        hasUser: !!response.data.user,
        // Not the response body: it carries the access token, and console output can be
        // captured off the device.
      })

      // VRG-490 §2 — a verified second factor withholds the session; the
      // caller completes it via workosAuthService.verifyChallenge(). Must be
      // checked BEFORE the tokens branch below, since neither shape carries
      // `tokens` and would otherwise fall through to "Authentication failed".
      if (response.data.challengeRequired) {
        return {
          success: true,
          challengeRequired: true,
          ticket: response.data.ticket,
          factorType: response.data.factorType,
          maskedPhone: response.data.maskedPhone,
          availableFactorTypes: response.data.availableFactorTypes,
          smsOptedOut: response.data.smsOptedOut,
        }
      }

      // VRG-490 D1 — a platform_admin with no verified factor at all; there
      // is nothing to challenge, they must enroll before a session exists.
      if (response.data.enrollmentRequired) {
        return { success: true, enrollmentRequired: true }
      }

      if (response.data.success && response.data.tokens) {
        console.log('[WorkOS Auth] Storing tokens in localStorage')
        // Store tokens
        // VRG-661: a new session never resumes the last one's company space.
        endVeraSpaceSession()
        storage.setItem('accessToken', response.data.tokens.accessToken)
        // The session credential is an HttpOnly cookie now (set by the API on this response);
        // drop any pre-registry refresh token rather than keep a JS-readable one around.
        storage.removeItem('refreshToken')

        console.log('[WorkOS Auth] Authentication successful, user:', response.data.user)
        return {
          success: true,
          user: response.data.user,
          isNewUser: response.data.isNewUser,
        }
      }

      console.warn('[WorkOS Auth] Authentication failed - no tokens in response')
      return {
        success: false,
        error: 'Authentication failed',
      }
    } catch (error) {
      console.error('[WorkOS Auth] Failed to verify Magic Auth code:', error)
      if (axios.isAxiosError(error)) {
        console.error('[WorkOS Auth] Error response:', error.response?.data)
      }
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Invalid or expired verification code'

      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  /**
   * Authenticate user with email and password via WorkOS
   *
   * VERA-1095: `errorCode` surfaces the API's structured error shape
   * (`{ message, code }`, see `HttpErrorDto`) so callers can branch on
   * `ErrorCode.AUTH_ACCOUNT_NOT_FOUND` (no account for this email) instead of
   * pattern-matching the human-readable message.
   */
  async loginWithPassword(email: string, password: string): Promise<{ success: boolean; user?: WorkOSUser; isNewUser?: boolean; error?: string; errorCode?: ErrorCode } & TwoFactorChallengeFields> {
    try {
      const response = await axios.post(
        `${SESSION_API_URL}/auth/password/login`,
        { email, password },
        { withCredentials: true, headers: PLATFORM_SESSION_HEADERS }, // sets the HttpOnly session cookie — see verifyMagicAuthCode
      )

      // VRG-490 §2/D1 — see the identical branch in verifyMagicAuthCode above
      // for the reasoning; must be checked before the tokens branch.
      if (response.data.challengeRequired) {
        return {
          success: true,
          challengeRequired: true,
          ticket: response.data.ticket,
          factorType: response.data.factorType,
          maskedPhone: response.data.maskedPhone,
          availableFactorTypes: response.data.availableFactorTypes,
          smsOptedOut: response.data.smsOptedOut,
        }
      }

      if (response.data.enrollmentRequired) {
        return { success: true, enrollmentRequired: true }
      }

      if (response.data.success && response.data.tokens) {
        // VRG-661: a new session never resumes the last one's company space.
        endVeraSpaceSession()
        storage.setItem('accessToken', response.data.tokens.accessToken)
        // The session credential is an HttpOnly cookie now (set by the API on this response);
        // drop any pre-registry refresh token rather than keep a JS-readable one around.
        storage.removeItem('refreshToken')
        return {
          success: true,
          user: response.data.user,
          isNewUser: response.data.isNewUser,
        }
      }

      return { success: false, error: 'Authentication failed' }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Invalid email or password'
      const errorCode = axios.isAxiosError(error)
        ? (error.response?.data?.code as ErrorCode | undefined)
        : undefined
      return { success: false, error: errorMessage, errorCode }
    }
  }

  /**
   * The challenge screen's "use another method" / "resend code" (Spencer, 2026-10-09: the user
   * chooses their second factor). All keyed by the opaque ticket; the API only ever lets a
   * ticket switch to factors it was issued with.
   */
  async getChallengeOptions(ticket: string): Promise<IAuthChallengeState | null> {
    try {
      const response = await axios.post(`${API_URL}/auth/2fa/challenge/options`, { ticket })
      return response.data as IAuthChallengeState
    } catch {
      return null
    }
  }

  /**
   * `errorCode` lets the challenge screen tell `SMS_RECIPIENT_OPTED_OUT` (the number replied
   * STOP — `error` is then the "text START to …" sentence) from a limit or a dead ticket.
   */
  async switchChallengeFactor(ticket: string, factorId: string): Promise<{ state?: IAuthChallengeState; error?: string; errorCode?: ErrorCode; retryAfterSeconds?: number }> {
    try {
      const response = await axios.post(`${API_URL}/auth/2fa/challenge/switch`, { ticket, factorId })
      return { state: response.data as IAuthChallengeState }
    } catch (error) {
      return {
        error: (axios.isAxiosError(error) && error.response?.data?.message) || 'Could not switch method. Please try again.',
        errorCode: axios.isAxiosError(error) ? (error.response?.data?.code as ErrorCode | undefined) : undefined,
        retryAfterSeconds: axios.isAxiosError(error) ? error.response?.data?.details?.retryAfterSeconds : undefined,
      }
    }
  }

  async resendChallengeCode(ticket: string): Promise<{ state?: IAuthChallengeState; error?: string; errorCode?: ErrorCode; retryAfterSeconds?: number }> {
    try {
      const response = await axios.post(`${API_URL}/auth/2fa/challenge/resend`, { ticket })
      return { state: response.data as IAuthChallengeState }
    } catch (error) {
      return {
        error: (axios.isAxiosError(error) && error.response?.data?.message) || 'Could not resend the code. Please try again.',
        errorCode: axios.isAxiosError(error) ? (error.response?.data?.code as ErrorCode | undefined) : undefined,
        retryAfterSeconds: axios.isAxiosError(error) ? error.response?.data?.details?.retryAfterSeconds : undefined,
      }
    }
  }

  /**
   * VRG-490 §2 — completes a login paused by a `challengeRequired` ticket
   * (from `verifyMagicAuthCode` / `loginWithPassword` above, or from the SSO
   * callback redirect's `?challengeRequired=true&ticket=…` — see
   * `AuthCallbackPage`). `code` is either the factor's own code (SMS/TOTP)
   * OR a recovery code — `POST /auth/2fa/challenge/verify` accepts both
   * through the same field.
   *
   * `@Public()` on the API side: there is no session yet by construction, so
   * this never sends an Authorization header.
   *
   * `expired` distinguishes a burned/unknown/expired ticket (HTTP 404 — no
   * point retrying, the caller should route back to sign-in) from an
   * incorrect code (HTTP 400 — the ticket is still alive, try again).
   */
  async verifyChallenge(ticket: string, code: string): Promise<{
    success: boolean
    user?: WorkOSUser
    isNewUser?: boolean
    usedRecoveryCode?: boolean
    remainingRecoveryCodes?: number
    error?: string
    expired?: boolean
  }> {
    try {
      const response = await axios.post(
        `${SESSION_API_URL}/auth/2fa/challenge/verify`,
        { ticket, code },
        { withCredentials: true, headers: PLATFORM_SESSION_HEADERS }, // sets the HttpOnly session cookie — see verifyMagicAuthCode
      )

      if (response.data.success && response.data.tokens) {
        // VRG-661: a new session never resumes the last one's company space.
        endVeraSpaceSession()
        storage.setItem('accessToken', response.data.tokens.accessToken)
        // The session credential is an HttpOnly cookie now (set by the API on this response);
        // drop any pre-registry refresh token rather than keep a JS-readable one around.
        storage.removeItem('refreshToken')
      }

      return {
        success: !!response.data.success,
        user: response.data.user,
        isNewUser: response.data.isNewUser,
        usedRecoveryCode: response.data.usedRecoveryCode,
        remainingRecoveryCodes: response.data.remainingRecoveryCodes,
      }
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      const expired = status === 404
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : expired
        ? 'This code has expired or too many attempts were made. Please sign in again.'
        : 'Incorrect code. Please try again.'
      return { success: false, error: errorMessage, expired }
    }
  }

  /**
   * Set password for the currently authenticated user via WorkOS
   */
  async setupPassword(password: string): Promise<{ success: boolean; error?: string }> {
    try {
      const token = this.getToken()
      if (!token) {
        return { success: false, error: 'Not authenticated' }
      }
      const response = await axios.post(
        `${API_URL}/auth/password/setup`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      return { success: response.data.success }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to set password'
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Request a password-reset link email (VERA-1095).
   *
   * Replaces the old dead-end behaviour that sent a 6-digit OTP and claimed
   * it was a "link" — this hits the real reset-request endpoint, which
   * emails a single-use WorkOS-backed link to /reset-password.
   *
   * `errorCode` mirrors loginWithPassword's contract: surfaces
   * ErrorCode.AUTH_ACCOUNT_NOT_FOUND when no account exists for the email
   * (deliberate, accepted enumeration tradeoff scoped to password flows —
   * see auth.service.ts).
   */
  async requestPasswordReset(email: string): Promise<{ success: boolean; error?: string; errorCode?: ErrorCode }> {
    try {
      const response = await axios.post(`${API_URL}/auth/password/reset/request`, { email })
      return { success: !!response.data?.success }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to send password reset email'
      const errorCode = axios.isAxiosError(error)
        ? (error.response?.data?.code as ErrorCode | undefined)
        : undefined
      return { success: false, error: errorMessage, errorCode }
    }
  }

  /**
   * Complete a password reset with the token from the emailed link
   * (VERA-1095). No auth header — the WorkOS token itself proves identity.
   */
  async confirmPasswordReset(token: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await axios.post(`${API_URL}/auth/password/reset/confirm`, { token, newPassword })
      return { success: !!response.data?.success }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : 'This reset link is invalid or has expired. Please request a new one.'
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Request (or resend) a signup email-verification link (VERA-1095).
   * Reachable right after signup and from the sign-in page's "Resend"
   * action when password login reports ErrorCode.AUTH_EMAIL_NOT_VERIFIED.
   */
  async requestEmailVerification(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await axios.post(`${API_URL}/auth/email/verify/request`, { email })
      return { success: !!response.data?.success }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to send verification email'
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Complete email verification with either credential from the emailed
   * message (VERA-1095): the link's opaque token (`email` omitted), or the
   * 6-digit fallback code shown in the same email (`email` REQUIRED — the
   * backend looks the code up by email and rate-limits attempts against it;
   * see `auth.workos.controller.ts`'s `confirmEmailVerification` docblock).
   * No auth header — the credential itself proves identity.
   */
  async confirmEmailVerification(token: string, email?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await axios.post(`${API_URL}/auth/email/verify`, { token, email })
      return { success: !!response.data?.success }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : 'This verification link is invalid or has expired. Please request a new one.'
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Initiate Google Sign In
   * Redirects to Google OAuth via WorkOS
   */
  loginWithGoogle(): void {
    const loginUrl = `${API_URL}/auth/login?provider=GoogleOAuth&${PLATFORM_SESSION_PARAM}`
    window.location.href = loginUrl
  }

  /**
   * THERE IS NO `loginWithMicrosoft`, AND THAT IS DELIBERATE.
   *
   * It existed and was removed. The previous note here said the connection was
   * "configured in the WorkOS dashboard (verified: staging returns a valid
   * login.microsoftonline.com authorize redirect)" — true, and the wrong thing
   * to have verified. The redirect IS valid; it goes to
   * `login.microsoftonline.com/consumers/...`, and `/consumers` serves personal
   * Microsoft accounts ONLY and rejects every work or school Entra ID account.
   *
   * That is WorkOS's design, not a setting we got wrong: their Microsoft OAuth
   * integration guide REQUIRES the Azure app registration to be "Personal
   * Microsoft accounts only". So for a B2B product the button fails for exactly
   * its intended users, and there is no configuration that changes it.
   *
   * The path for work/school accounts is WorkOS Entra ID OIDC — an enterprise
   * SSO connection set up PER ORGANIZATION, which by construction cannot serve
   * a self-serve signup (no org exists yet at that point). If Vera adds it, it
   * belongs behind an authenticated org-settings surface, and it will NOT reuse
   * this method — hence removing rather than leaving it dormant for someone to
   * wire a button back onto.
   *
   * See `SignInPage`'s `appleLoginEnabled` block for the full reasoning.
   */

  /**
   * Initiate Apple Sign In
   * Redirects to Apple OAuth via WorkOS. GATED OFF by default
   * (`VITE_ENABLE_APPLE_LOGIN`) until Vera owns its own Apple Services ID: the
   * connection currently runs on WorkOS's shared default credential, and Apple
   * scopes `sub` and Hide-My-Email relay addresses to the developer TEAM, so
   * switching later invalidates every account created before the switch. See
   * `SignInPage` for the full note.
   */
  loginWithApple(): void {
    const loginUrl = `${API_URL}/auth/login?provider=AppleOAuth&${PLATFORM_SESSION_PARAM}`
    window.location.href = loginUrl
  }

  /**
   * Get login URL for SSO domain
   */
  async getLoginUrl(domain?: string): Promise<string> {
    // If domain is provided, use SSO login
    if (domain) {
      return `${API_URL}/auth/login?domain=${encodeURIComponent(domain)}&${PLATFORM_SESSION_PARAM}`
    }
    // Otherwise, use magic auth or default login
    return `${API_URL}/auth/login?${PLATFORM_SESSION_PARAM}`
  }

  /**
   * Handle OAuth redirect after login
   */
  async handleLoginRedirect(urlParams: URLSearchParams): Promise<{ user: WorkOSUser } | null> {
    try {
      const code = urlParams.get('code')
      if (!code) {
        return null
      }

      const response = await axios.get(`${SESSION_API_URL}/auth/callback`, {
        params: { code },
        withCredentials: true,
      })

      if (response.data.tokens) {
        // VRG-661: a new session never resumes the last one's company space.
        endVeraSpaceSession()
        storage.setItem('accessToken', response.data.tokens.accessToken)
        // The session credential is an HttpOnly cookie now (set by the API on this response);
        // drop any pre-registry refresh token rather than keep a JS-readable one around.
        storage.removeItem('refreshToken')

        return {
          user: response.data.user,
        }
      }

      return null
    } catch (error) {
      console.error('Failed to handle login redirect:', error)
      return null
    }
  }
}

export const workosAuthService = new WorkOSAuthService()
